import React from 'react';
import reduce from 'lodash/reduce';
import loadable from '@loadable/component';

import { isMediumDown } from '@eventbrite/eds-utils';
import { CardList } from '@eventbrite/eds-card-list';
import { ExpandableCardContent } from '@eventbrite/eds-expandable-card-content';
import FacebookLoginButton from './FacebookLoginButton';
import AppleLoginButton from './AppleLoginButton';
import GoogleLoginButton from './GoogleLoginButton';
import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
    OTHER_LOGIN_METHODS,
    OTHER_SIGN_UP_METHODS,
} from './constants';

import './SocialLogin.scss';

export const SOCIAL_MAP = {
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE]: GoogleLoginButton,
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK]: FacebookLoginButton,
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE]: AppleLoginButton,
};

const SocialLoginButtons = ({
    isCheckoutAuthenticationFlow,
    onSocialConnectVerified,
    onSocialConnectNotAuthorized,
    onSocialLogInUserAuthorized,
    handleOnLoadComplete,
    handleOnComponentUpdated,
    handleSocialProviderClick,
    onNotConnected,
    socialSSOSettings = {},
    buttonAppleText,
    buttonFBText,
    buttonGoogleText,
    asyncStrategy,
    fp,
    disabledOptions = [],
    secondarySocialMethods,
    isSignupFlow,
}) => {
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        setIsMobile(isMediumDown());
        const updateSecondaryMethods = () => {
            setIsMobile(isMediumDown());
        };
        window.addEventListener('resize', updateSecondaryMethods);

        return () =>
            window.removeEventListener('resize', updateSecondaryMethods);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const secondaryMethods = [];
    let secondaryMethodIndexCounter = 0;
    const primaryMethods = reduce(
        SOCIAL_MAP,
        (memo, SocialLoginButton, socialProvider) => {
            const settings = socialSSOSettings[socialProvider];

            const _handleOnClick = (socialProvider, state) => {
                if (fp) {
                    const DeviceFingerprint = loadable((props) =>
                        import('@eventbrite/device-fingerprint'),
                    );

                    DeviceFingerprint.load().then((component) => {
                        component.create({ data: { fpid: '' } });
                    });
                }

                handleSocialProviderClick(socialProvider, state);
            };

            const classes = secondarySocialMethods?.[socialProvider]
                ? 'secondary-method'
                : 'eds-l-mar-bot-2';

            if (settings && !disabledOptions.includes(socialProvider)) {
                const component = (
                    <div key={socialProvider} className={classes}>
                        <SocialLoginButton
                            isCheckoutAuthenticationFlow={
                                isCheckoutAuthenticationFlow
                            }
                            onSocialConnectVerified={onSocialConnectVerified}
                            onSocialConnectNotAuthorized={
                                onSocialConnectNotAuthorized
                            }
                            onSocialLogInUserAuthorized={
                                onSocialLogInUserAuthorized
                            }
                            onClick={_handleOnClick}
                            handleOnLoadComplete={handleOnLoadComplete}
                            handleOnComponentUpdated={handleOnComponentUpdated}
                            handleOnNotConnected={onNotConnected}
                            buttonAppleText={buttonAppleText}
                            buttonFBText={buttonFBText}
                            buttonGoogleText={buttonGoogleText}
                            asyncStrategy={asyncStrategy}
                            isSecondary={
                                secondarySocialMethods?.[socialProvider]
                            }
                            {...settings}
                        />
                    </div>
                );
                if (secondarySocialMethods?.[socialProvider]) {
                    secondaryMethodIndexCounter++;

                    const secondaryMethod = {
                        id: secondaryMethodIndexCounter.toString(),
                        content: component,
                    };
                    secondaryMethods.push(secondaryMethod);
                } else {
                    return [...memo, component];
                }
            }

            return memo;
        },
        [],
    );

    const getOtherLoinOptions = () => {
        const label = (
            <p>{isSignupFlow ? OTHER_SIGN_UP_METHODS : OTHER_LOGIN_METHODS}</p>
        );

        const otherLoginOptions = isMobile ? (
            <ExpandableCardContent items={secondaryMethods}>
                {label}
            </ExpandableCardContent>
        ) : (
            <div>
                {label}
                <CardList items={secondaryMethods} />
            </div>
        );
        return otherLoginOptions;
    };

    const result = secondaryMethods.length ? (
        <div>
            <div className="primary-methods">{primaryMethods}</div>
            <div
                className="other-login-options"
                data-automation="other-login-options"
            >
                {getOtherLoinOptions()}
            </div>
        </div>
    ) : (
        primaryMethods
    );
    return result;
};

export default SocialLoginButtons;
